import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined';
import InsertPhotoIcon from '@mui/icons-material/InsertPhotoOutlined';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import {
  MAX_SECTION_HEIGHT,
  MAX_SECTION_WIDTH,
  MIN_SECTION_HEIGHT,
  MIN_SECTION_WIDTH,
} from '../../layoutUtils';
import { HMIField } from '../../types';

export const ROW_UNIT_PX = 50;

const KIND_LABEL: Record<HMIField['kind'], string> = {
  indicator: 'Indicator',
  control: 'Control',
  action: 'Action',
  chart: 'Widget',
  image: 'Image',
};

export interface SectionBoxProps {
  label: string;
  width: number;
  height: number;
  fields: HMIField[];
  readOnly?: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  /** Pixel width of the 12-column canvas, used to translate drag deltas into column units. */
  containerWidthPx: number;
  onResize: (size: { width: number; height: number }) => void;
  onRename: () => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
  onAddField: () => void;
  onAddWidget: () => void;
  onAddImage: () => void;
  onEditField: (field: HMIField) => void;
  onDeleteField: (field: HMIField) => void;
  onMoveField: (field: HMIField, direction: -1 | 1) => void;
}

/**
 * One section tile on the layout builder's canvas. Mirrors the drag-to-resize
 * pattern used by the dashboard's `ResizableWidget`, but self-contained (no
 * dependency on the dashboard `WidgetConfig` type) and scoped to a 12-column
 * grid + row-unit height so it matches the same grid the live HMI renderer
 * (`HMIRenderer`) uses.
 */
function SectionBox({
  label,
  width,
  height,
  fields,
  readOnly,
  canMoveUp,
  canMoveDown,
  containerWidthPx,
  onResize,
  onRename,
  onDelete,
  onMove,
  onAddField,
  onAddWidget,
  onAddImage,
  onEditField,
  onDeleteField,
  onMoveField,
}: SectionBoxProps) {
  const fieldCount = fields.length;
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, width, height });

  const colWidthPx = containerWidthPx > 0 ? containerWidthPx / 12 : 60;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      dragStart.current = { x: e.clientX, y: e.clientY, width, height };
      setDragging(true);
    },
    [readOnly, width, height]
  );

  useEffect(() => {
    if (!dragging) return undefined;

    const handleMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStart.current.x;
      const deltaY = e.clientY - dragStart.current.y;
      const newWidth = Math.max(
        MIN_SECTION_WIDTH,
        Math.min(
          MAX_SECTION_WIDTH,
          Math.round(dragStart.current.width + deltaX / colWidthPx)
        )
      );
      const newHeight = Math.max(
        MIN_SECTION_HEIGHT,
        Math.min(
          MAX_SECTION_HEIGHT,
          Math.round(dragStart.current.height + deltaY / ROW_UNIT_PX)
        )
      );
      if (newWidth !== width || newHeight !== height) {
        onResize({ width: newWidth, height: newHeight });
      }
    };
    const handleUp = () => setDragging(false);

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, colWidthPx, width, height, onResize]);

  return (
    <Box
      sx={{
        position: 'relative',
        gridColumn: `span ${Math.min(width, 12)}`,
        height: height * ROW_UNIT_PX,
        border: '1px dashed',
        borderColor: dragging ? 'primary.main' : 'grayscale.400',
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: dragging ? 'none' : 'border-color 0.15s',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap title={label}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {width} cols x {height} rows &middot; {fieldCount} field
            {fieldCount === 1 ? '' : 's'}
          </Typography>
        </Box>
        {!readOnly && (
          <Stack direction="row" spacing={0}>
            <Tooltip title="Move earlier">
              <span>
                <IconButton size="small" disabled={!canMoveUp} onClick={() => onMove(-1)}>
                  <ArrowUpwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move later">
              <span>
                <IconButton size="small" disabled={!canMoveDown} onClick={() => onMove(1)}>
                  <ArrowDownwardIcon fontSize="inherit" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Rename section">
              <IconButton size="small" onClick={onRename}>
                <DriveFileRenameOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete section">
              <IconButton size="small" onClick={onDelete}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          mt: 0.5,
          pr: readOnly ? 0 : 2.5,
        }}
      >
        {fields.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            No fields yet.
          </Typography>
        )}
        {[...fields]
          .sort((a, b) => a.order - b.order)
          .map((field, idx) => (
            <Stack
              key={field.id}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                py: 0.25,
                borderBottom: '1px solid rgba(0,0,0,0.06)',
                '&:hover .field-actions': { opacity: 1 },
              }}
            >
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body2" noWrap title={field.caption}>
                  {field.caption}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {KIND_LABEL[field.kind]} &middot; {field.datatype}
                  {field.kind === 'chart' && field.chartType ? ` (${field.chartType})` : ''}
                  {field.kind === 'image' ? ` (${(field.overlayItems ?? []).length} pin(s))` : ''}
                </Typography>
              </Box>
              {!readOnly && (
                <Stack
                  direction="row"
                  spacing={0}
                  className="field-actions"
                  sx={{ opacity: 0, transition: 'opacity 0.1s' }}
                >
                  <Tooltip title="Move earlier">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === 0}
                        onClick={() => onMoveField(field, -1)}
                      >
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move later">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === fields.length - 1}
                        onClick={() => onMoveField(field, 1)}
                      >
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit field">
                    <IconButton size="small" onClick={() => onEditField(field)}>
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete field">
                    <IconButton size="small" onClick={() => onDeleteField(field)}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          ))}
      </Box>

      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={onAddField}>
            Field
          </Button>
          <Button size="small" startIcon={<InsertChartIcon />} onClick={onAddWidget}>
            Widget
          </Button>
          <Button size="small" startIcon={<InsertPhotoIcon />} onClick={onAddImage}>
            Image
          </Button>
        </Stack>
      )}

      {!readOnly && (
        <Tooltip title="Drag to resize">
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 20,
              height: 20,
              cursor: 'nwse-resize',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
              color: 'primary.main',
              opacity: 0.55,
              '&:hover': { opacity: 1 },
            }}
          >
            <OpenWithIcon sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

export default SectionBox;
